import type { TeacherCohortAssignmentProgressRow, TeacherCohortRecord } from "../../auth/api-client";
import { PROGRESS_CHART_COLORS } from "../../student/assignmentProgressStatus";

export type CohortProgressBundle = {
  cohortId: string;
  cohortLabel: string;
  courseTitle: string;
  status: TeacherCohortRecord["status"];
  rosterTotal: number;
  assignments: TeacherCohortAssignmentProgressRow[];
};

export type ProgressTotals = {
  complete: number;
  inProgress: number;
  needsHelp: number;
  notStarted: number;
  total: number;
  passRate: number | null;
  rosterStudents: number;
  liveLessons: number;
  cohortCount: number;
};

export type LessonProgressRow = {
  cohortId: string;
  cohortLabel: string;
  assignmentId: string;
  itemKind: "lesson" | "quiz";
  lessonId: string | null;
  quizId: string | null;
  lessonTitle: string;
  complete: number;
  inProgress: number;
  needsHelp: number;
  notStarted: number;
  rosterTotal: number;
  passRate: number | null;
};

export function teacherAssignmentReviewUrl(
  cohortId: string,
  item: Pick<LessonProgressRow, "itemKind" | "lessonId" | "quizId" | "lessonTitle">
): string | null {
  if (item.itemKind === "quiz" && item.quizId) {
    return `/teacher/cohorts/${encodeURIComponent(cohortId)}/quizzes/${encodeURIComponent(item.quizId)}`;
  }
  if (item.lessonId) {
    const qs = new URLSearchParams({ title: item.lessonTitle });
    return `/teacher/cohorts/${encodeURIComponent(cohortId)}/lessons/${encodeURIComponent(item.lessonId)}/submissions?${qs.toString()}`;
  }
  return null;
}

export type CohortPassRateRow = {
  cohortId: string;
  cohortLabel: string;
  courseTitle: string;
  passRate: number | null;
  complete: number;
  total: number;
  rosterTotal: number;
};

export function aggregateProgress(bundles: CohortProgressBundle[]): ProgressTotals {
  let complete = 0;
  let inProgress = 0;
  let needsHelp = 0;
  let notStarted = 0;
  let rosterStudents = 0;
  let liveLessons = 0;

  for (const b of bundles) {
    rosterStudents += b.rosterTotal;
    liveLessons += b.assignments.length;
    for (const a of b.assignments) {
      complete += a.complete;
      inProgress += a.inProgress;
      needsHelp += a.needsHelp;
      notStarted += a.notStarted;
    }
  }

  const total = complete + inProgress + needsHelp + notStarted;
  return {
    complete,
    inProgress,
    needsHelp,
    notStarted,
    total,
    passRate: total > 0 ? Math.round((complete / total) * 100) : null,
    rosterStudents,
    liveLessons,
    cohortCount: bundles.length,
  };
}

export type LessonRowSort = "needs-attention" | "plan-order";

function sortLessonRowsByNeedsAttention(rows: LessonProgressRow[]): LessonProgressRow[] {
  return rows.sort((x, y) => {
    if (y.needsHelp !== x.needsHelp) return y.needsHelp - x.needsHelp;
    if (y.inProgress !== x.inProgress) return y.inProgress - x.inProgress;
    if ((y.passRate ?? -1) !== (x.passRate ?? -1)) return (y.passRate ?? -1) - (x.passRate ?? -1);
    return x.lessonTitle.localeCompare(y.lessonTitle);
  });
}

export function lessonProgressRowFromAssignment(
  bundle: Pick<CohortProgressBundle, "cohortId" | "cohortLabel">,
  a: TeacherCohortAssignmentProgressRow
): LessonProgressRow {
  const t = a.complete + a.inProgress + a.needsHelp + a.notStarted;
  return {
    cohortId: bundle.cohortId,
    cohortLabel: bundle.cohortLabel,
    assignmentId: a.assignmentId,
    itemKind: a.itemKind,
    lessonId: a.lessonId,
    quizId: a.quizId,
    lessonTitle: a.lessonTitle,
    complete: a.complete,
    inProgress: a.inProgress,
    needsHelp: a.needsHelp,
    notStarted: a.notStarted,
    rosterTotal: a.rosterTotal,
    passRate: t > 0 ? Math.round((a.complete / t) * 100) : null,
  };
}

/** Assignment rows in cohort plan order (matches API assignment-progress ordering). */
export function lessonRowsFromBundle(bundle: CohortProgressBundle): LessonProgressRow[] {
  return bundle.assignments.map((a) => lessonProgressRowFromAssignment(bundle, a));
}

export function lessonRowsFromBundles(
  bundles: CohortProgressBundle[],
  options?: { sort?: LessonRowSort }
): LessonProgressRow[] {
  const rows: LessonProgressRow[] = [];
  for (const b of bundles) {
    for (const a of b.assignments) {
      rows.push(lessonProgressRowFromAssignment(b, a));
    }
  }
  const sort = options?.sort ?? "needs-attention";
  return sort === "plan-order" ? rows : sortLessonRowsByNeedsAttention(rows);
}

export function cohortPassRates(bundles: CohortProgressBundle[]): CohortPassRateRow[] {
  return bundles
    .map((b) => {
      let complete = 0;
      let total = 0;
      for (const a of b.assignments) {
        complete += a.complete;
        total += a.complete + a.inProgress + a.needsHelp + a.notStarted;
      }
      return {
        cohortId: b.cohortId,
        cohortLabel: b.cohortLabel,
        courseTitle: b.courseTitle,
        passRate: total > 0 ? Math.round((complete / total) * 100) : null,
        complete,
        total,
        rosterTotal: b.rosterTotal,
      };
    })
    .sort((a, b) => (b.passRate ?? -1) - (a.passRate ?? -1));
}

export function donutGradient(
  totals: Pick<ProgressTotals, "complete" | "inProgress" | "needsHelp" | "notStarted">
): string {
  const sum = totals.complete + totals.inProgress + totals.needsHelp + totals.notStarted;
  if (sum <= 0) return "conic-gradient(rgba(27, 77, 46, 0.12) 0deg 360deg)";
  const p1 = (totals.complete / sum) * 360;
  const p2 = p1 + (totals.inProgress / sum) * 360;
  const p3 = p2 + (totals.needsHelp / sum) * 360;
  return `conic-gradient(
    ${PROGRESS_CHART_COLORS.complete} 0deg ${p1}deg,
    ${PROGRESS_CHART_COLORS.inProgress} ${p1}deg ${p2}deg,
    ${PROGRESS_CHART_COLORS.needsHelp} ${p2}deg ${p3}deg,
    ${PROGRESS_CHART_COLORS.notStarted} ${p3}deg 360deg
  )`;
}
